import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, Check, X, Send, Target,
  Edit2, Calendar as CalIcon, MessageSquare, Sparkles, Loader2,
  StickyNote, Maximize2, Minimize2, Trash2, Settings, FileText, GripVertical
} from 'lucide-react';
import strategyMd from '../strategy.md?raw';

// ---------- Date utilities ----------
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const startOfWeek = (d) => {
  const r = new Date(d); const day = (r.getDay() + 6) % 7;
  r.setDate(r.getDate() - day); r.setHours(0, 0, 0, 0); return r;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const addMonths = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; };
const sameDay = (a, b) => fmtDate(a) === fmtDate(b);
const weekNum = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
};
const quarterOf = (d) => Math.floor(d.getMonth() / 3) + 1;
const quarterKey = (d) => `${d.getFullYear()}-Q${quarterOf(d)}`;
const periodStart = (d) => {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate() <= 15 ? 1 : 16);
  r.setHours(0, 0, 0, 0); return r;
};
const periodEnd = (d) => {
  const r = d.getDate() <= 15
    ? new Date(d.getFullYear(), d.getMonth(), 15)
    : new Date(d.getFullYear(), d.getMonth() + 1, 0);
  r.setHours(0, 0, 0, 0); return r;
};
const periodKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-H${d.getDate() <= 15 ? 1 : 2}`;
const periodDays = (d) => {
  const start = periodStart(d), end = periodEnd(d);
  const out = []; const cur = new Date(start);
  while (cur <= end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return out;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// ---------- ITG invoice generation ----------
const ITG_RATE = 58;
async function generateITGInvoiceDocx({ periodKey, dougHrs, dustyHrs, invoiceNo, invoiceDate }) {
  const useBoth = dustyHrs > 0;
  const templateUrl = useBoth ? '/templates/itg-invoice-both.docx' : '/templates/itg-invoice-doug.docx';

  const dougSub = dougHrs * ITG_RATE;
  const dustySub = dustyHrs * ITG_RATE;
  const totalHrs = dougHrs + dustyHrs;
  const totalAmt = totalHrs * ITG_RATE;

  const fmtH = (h) => h % 1 === 0 ? String(h) : h.toFixed(1).replace(/\.0$/, '');
  const fmtSub = (n) => n.toLocaleString('en-US', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  const fmtTot = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const [yearStr, monthStr, halfStr] = periodKey.split('-');
  const year = parseInt(yearStr, 10);
  const monthIdx = parseInt(monthStr, 10) - 1;
  const monthName = MONTHS_FULL[monthIdx];
  const startDay = halfStr === 'H1' ? 1 : 16;
  const endDay = halfStr === 'H1' ? 15 : new Date(year, monthIdx + 1, 0).getDate();
  const periodLabel = `${monthName} ${startDay}-${endDay}, ${year}.`;

  const dateStr = `${pad(invoiceDate.getMonth() + 1)}/${pad(invoiceDate.getDate())}/${invoiceDate.getFullYear()}`;
  const halfWord = halfStr === 'H1' ? 'First' : 'Second';
  const filename = `John Lloyd Invoice ${year} ${monthName} ${halfWord} Half.docx`;

  const [{ default: PizZip }, { saveAs }] = await Promise.all([
    import('pizzip'),
    import('file-saver'),
  ]);

  const res = await fetch(templateUrl);
  if (!res.ok) throw new Error(`template fetch failed: ${res.status}`);
  const buf = await res.arrayBuffer();
  const zip = new PizZip(buf);
  let xml = zip.file('word/document.xml').asText();

  const escapeXml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const replacements = {
    '{INV_NO}': String(invoiceNo),
    '{INV_DATE}': dateStr,
    '{PERIOD}': periodLabel,
    '{DOUG_HRS}': fmtH(dougHrs),
    '{DOUG_SUB}': fmtSub(dougSub),
    '{DUSTY_HRS}': fmtH(dustyHrs),
    '{DUSTY_SUB}': fmtSub(dustySub),
    '{TOTAL_HRS}': fmtH(totalHrs),
    '{TOTAL}': fmtTot(totalAmt),
  };
  for (const [k, v] of Object.entries(replacements)) {
    xml = xml.split(k).join(escapeXml(v));
  }

  zip.file('word/document.xml', xml);
  const out = zip.generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });
  saveAs(out, filename);
}

// ---------- Default state ----------
const DEFAULT_STATE = {
  goals: {
    northStar: "€1M+ company via CAD Bridge SaaS + Clarys. €500K+ net worth by 2029. Coop SaaS preserved as slow-burn optionality.",
    yearly: {
      2026: "Clarys €60–90K. CAD Bridge: Dusty pulse-check → Doug → path decision → first external pilot. Coop slow-burn.",
      2027: "Combined €350–600K. CAD Bridge: external pilots live, paying customers, MRR climbing. Youngest in school.",
      2028: "CAD Bridge primary business; ARR target refresh after Year 2 path decision. Clarys retainer-only or wound down."
    },
    currentQuarter: "Rates up. Cleanup Sprint launch. CAD Bridge pulse-check (Dusty). Coop on slow-burn.",
    nonNegotiables: [
      "Friday 90-min review",
      "Min 6 hrs/week on primary SaaS thesis (CAD Bridge) from Q3 2026",
      "7 hours sleep nightly"
    ]
  },
  hoursTargets: { ITG: 20, Clarys: 11 },
  clarysPeriods: {},
  quarters: {
    "2026-Q2": { focus: "Rates up. Cleanup Sprint launch. CAD Bridge pulse-check.", milestones: [
      "Raise Clarys rate to €150/hr on new clients",
      "Light Dusty conversation (CAD Bridge pulse-check)",
      "CAD Bridge architecture in private repo (timestamped)",
      "Doug conversation scheduled or completed",
      "First external AV firm contact",
      "1 new Clarys client at €150+/hr (Cleanup Sprint launch)",
      "2 LinkedIn case studies published",
      "2 peer coop interviews (slow-burn; Hosenlander + Biobulkbende)"
    ]},
    "2026-Q3": { focus: "CAD Bridge Phase 3 path decision. LinkedIn content cadence.", milestones: [
      "CAD Bridge commercialization-path chosen (spin-out / license-back / internal venture / side project)",
      "CAD Bridge engine v2 architecture",
      "LinkedIn 2x/week",
      "1 new Clarys client",
      "Accountant consult: BV + holding structure"
    ]},
    "2026-Q4": { focus: "First external AV pilot. Maerten wraps. Holding BV.", milestones: [
      "First external AV firm pilot live (CAD Bridge)",
      "Maerten / Bos BSO pilot wraps; ongoing rate set above community-tier",
      "4 active Clarys clients",
      "Holding BV incorporated (before primary SaaS thesis has paying customers)"
    ]},
    "2027-Q1": { focus: "Monetize CAD Bridge.", milestones: [
      "First paying CAD Bridge customer",
      "CAD Bridge engine v2 design"
    ]},
    "2027-Q2": { focus: "Scale CAD Bridge to 5–10 paying customers.", milestones: [
      "€1–3K MRR",
      "Clarys rate to €180–200/hr",
      "ITG reduced to 15 hrs/week"
    ]},
    "2027-Q3": { focus: "Team leverage.", milestones: [
      "15–25 paying CAD Bridge seats",
      "First contractor hired"
    ]},
    "2027-Q4": { focus: "ITG decision.", milestones: [
      "30–50 paying CAD Bridge seats",
      "€7–15K MRR",
      "ITG end or retain as passive"
    ]},
    "2028-Q1": { focus: "CAD Bridge primary business.", milestones: [
      "€10–20K MRR",
      "Engine v3 / pricing-DB independence milestone"
    ]},
    "2028-Q2": { focus: "Expansion evaluation.", milestones: [
      "€15–30K MRR",
      "US/EU AV-firm expansion pilot"
    ]},
    "2028-Q3": { focus: "Scale or raise.", milestones: [
      "Decision: bootstrap vs seed",
      "1–2 team members"
    ]},
    "2028-Q4": { focus: "Year 3 close.", milestones: [
      "€20–60K MRR",
      "Net worth target assessed"
    ]}
  },
  days: {},
  chats: [],
  notes: [],
  theme: 'blue',
  invoiceCounters: { ITG: 2606 },
};

function normalizeNotes(n) {
  if (Array.isArray(n)) return n;
  if (typeof n === 'string' && n.trim()) {
    return n.split('\n').map(l => l.trim()).filter(Boolean)
      .map((text, i) => ({ id: Date.now() + i + Math.random(), text }));
  }
  return [];
}

function normalizeChat(c) {
  if (Array.isArray(c)) return c;
  if (c && typeof c === 'object') {
    const flat = [];
    for (const [date, msgs] of Object.entries(c)) {
      if (!Array.isArray(msgs)) continue;
      for (const m of msgs) flat.push({ ...m, date: m.date || date });
    }
    flat.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return flat;
  }
  return [];
}

// ---------- Themes ----------
const THEMES = {
  blue:   { name: 'Blue',   body: '#EEF4FA', tint: '#DAE5EF', softTint: '#E4ECF4', bar: '#F4F7FB', tintAlpha: 'rgba(218, 229, 239, 0.88)', accent: '#BBCDDE', strong: '#0369A1' },
  rust:   { name: 'Rust',   body: '#FBF1EB', tint: '#EFD5C4', softTint: '#F5E2D3', bar: '#FBF6F1', tintAlpha: 'rgba(239, 213, 196, 0.88)', accent: '#DEB7A0', strong: '#B0501F' },
  wine:   { name: 'Wine',   body: '#F7ECEF', tint: '#E4C4CE', softTint: '#EFD8DE', bar: '#FAF3F5', tintAlpha: 'rgba(228, 196, 206, 0.88)', accent: '#C99AA7', strong: '#9B2C44' },
  forest: { name: 'Forest', body: '#EFF6F1', tint: '#D2E5D7', softTint: '#DFEDE3', bar: '#F4F8F5', tintAlpha: 'rgba(210, 229, 215, 0.88)', accent: '#A4C7AB', strong: '#15803D' },
};

// ---------- Storage (localStorage cache + Netlify Blobs sync) ----------
const STORAGE_KEY = 'operator_state_v1';
const SYNC_URL = '/.netlify/functions/state';

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const merged = { ...DEFAULT_STATE, ...JSON.parse(raw) };
      merged.quarters = DEFAULT_STATE.quarters;
      merged.hoursTargets = DEFAULT_STATE.hoursTargets;
      merged.notes = normalizeNotes(merged.notes);
      merged.chats = normalizeChat(merged.chats);
      return merged;
    }
  } catch (e) { /* first run or quota */ }
  return DEFAULT_STATE;
}
function saveLocal(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch (e) { console.error('local save failed', e); }
}

async function fetchRemote() {
  const r = await fetch(SYNC_URL, { method: 'GET', cache: 'no-store' });
  if (!r.ok) throw new Error(`GET ${r.status}`);
  return r.json(); // { state, updatedAt }
}
async function pushRemote(state) {
  const r = await fetch(SYNC_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  });
  if (!r.ok) throw new Error(`PUT ${r.status}`);
  return r.json();
}

// Real data = remote blob has days or chats populated.
function hasRealData(s) {
  if (!s || typeof s !== 'object') return false;
  const daysCount = s.days ? Object.keys(s.days).length : 0;
  let chatsCount = 0;
  if (Array.isArray(s.chats)) chatsCount = s.chats.length;
  else if (s.chats && typeof s.chats === 'object') {
    chatsCount = Object.values(s.chats).reduce((a, v) => a + (Array.isArray(v) ? v.length : 0), 0);
  }
  const hasNotes = Array.isArray(s.notes)
    ? s.notes.length > 0
    : (typeof s.notes === 'string' && s.notes.trim().length > 0);
  return daysCount + chatsCount > 0 || hasNotes;
}

// ---------- Claude chat (via Netlify Function proxy) ----------
async function callClaude(systemPrompt, messages) {
  const response = await fetch('/.netlify/functions/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ system: systemPrompt, messages })
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`API ${response.status}${text ? ` — ${text}` : ''}`);
  }
  const data = await response.json();
  return data.content.filter(b => b.type === 'text').map(b => b.text).join('\n');
}

function buildSystemPrompt(state, today) {
  const todayKey = fmtDate(today);
  const qKey = quarterKey(today);
  const q = state.quarters[qKey];
  const day = state.days[todayKey] || { items: [], notes: '' };
  const year = today.getFullYear();
  const yearGoal = state.goals.yearly[year] || '';
  const recent = Object.entries(state.days)
    .filter(([k]) => k < todayKey)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${v.items.filter(i => i.done).length}/${v.items.length} done${v.notes ? ' — ' + v.notes : ''}`)
    .join('\n');

  const tgt = state.hoursTargets;
  const ITG_SUB_CLIENTS = ['Dusty'];
  const summarizePeriod = (anchor) => {
    const pd = periodDays(anchor);
    const pk = periodKey(anchor);
    const pStart = periodStart(anchor), pEnd = periodEnd(anchor);
    const label = `${MONTHS[pStart.getMonth()]} ${pStart.getDate()}–${pEnd.getDate()}, ${pStart.getFullYear()}`;
    const scalarSum = (stream) => pd.reduce((s, d) => s + (state.days[fmtDate(d)]?.hours?.[stream] || 0), 0);
    const dailyBreakdown = (getHours) => pd
      .map(d => { const h = getHours(d); return h > 0 ? `${MONTHS[d.getMonth()]} ${d.getDate()}: ${h}h` : null; })
      .filter(Boolean)
      .join(', ');
    const scale = pd.length / 7;
    const pTgt = (w) => Math.round(w * scale * 2) / 2;

    const itgBase = scalarSum('ITG');
    const itgBaseDaily = itgBase > 0 ? dailyBreakdown(d => state.days[fmtDate(d)]?.hours?.ITG || 0) : '';
    const itgSubBreakdown = ITG_SUB_CLIENTS
      .map(sub => {
        const total = scalarSum(sub);
        if (total <= 0) return null;
        const daily = dailyBreakdown(d => state.days[fmtDate(d)]?.hours?.[sub] || 0);
        return { client: sub, total, daily };
      })
      .filter(Boolean);
    const itgTotal = itgBase + ITG_SUB_CLIENTS.reduce((s, sub) => s + scalarSum(sub), 0);

    const clarysBase = scalarSum('Clarys');
    const clarysBaseDaily = clarysBase > 0 ? dailyBreakdown(d => state.days[fmtDate(d)]?.hours?.Clarys || 0) : '';
    const rows = (state.clarysPeriods || {})[pk] || [];
    const clarysBreakdown = rows
      .map(r => {
        const total = pd.reduce((s, d) => s + (r.dayHours?.[fmtDate(d)] || 0), 0);
        if (total <= 0) return null;
        const daily = dailyBreakdown(d => r.dayHours?.[fmtDate(d)] || 0);
        return { client: r.client || '(unnamed)', total, daily };
      })
      .filter(Boolean);
    const clarysClientsTotal = rows.reduce((s, r) => s + pd.reduce((t, d) => t + (r.dayHours?.[fmtDate(d)] || 0), 0), 0);
    const clarysTotal = clarysBase + clarysClientsTotal;
    const summary = [
      `ITG ${itgTotal}/${pTgt(tgt.ITG)}`,
      `Clarys ${clarysTotal}/${pTgt(tgt.Clarys)}`,
    ].join(' | ');
    const itgLines = (itgBase > 0 || itgSubBreakdown.length)
      ? [
          itgBase > 0 ? `  (base / unassigned) — ${itgBase}h${itgBaseDaily ? ` (${itgBaseDaily})` : ''}` : null,
          ...itgSubBreakdown.map(c => `  ${c.client} — ${c.total}h${c.daily ? ` (${c.daily})` : ''}`)
        ].filter(Boolean).join('\n')
      : '  (no ITG hours logged)';
    const clarysLines = (clarysBase > 0 || clarysBreakdown.length)
      ? [
          clarysBase > 0 ? `  (base / unassigned) — ${clarysBase}h${clarysBaseDaily ? ` (${clarysBaseDaily})` : ''}` : null,
          ...clarysBreakdown.map(c => `  ${c.client} — ${c.total}h${c.daily ? ` (${c.daily})` : ''}`)
        ].filter(Boolean).join('\n')
      : '  (no Clarys hours logged)';
    return { label, summary, itgLines, clarysLines, hasAny: itgTotal + clarysTotal > 0 };
  };

  const cur = summarizePeriod(today);
  const prevAnchor = new Date(periodStart(today).getFullYear(), periodStart(today).getMonth(), periodStart(today).getDate() - 1);
  const prev = summarizePeriod(prevAnchor);
  const pLabel = cur.label;

  return `You are Johnny's strategic operator AI. This chat is a single continuous thread across all dates — you see history from prior days. Messages prefixed like "[2026-04-20] ..." came from that date; un-prefixed messages are from today's selected context. You can freely reference and build on things said on other dates.

Context date (the day currently selected): ${todayKey}

NORTH STAR: ${state.goals.northStar}

${year} GOAL: ${yearGoal}

CURRENT QUARTER (${qKey}): ${q?.focus || ''}
Milestones: ${(q?.milestones || []).join(' | ')}

NON-NEGOTIABLES: ${state.goals.nonNegotiables.join(' | ')}

TODAY (${todayKey}):
Items: ${day.items.length ? day.items.map(i => `${i.done?'✓':'○'} ${i.text}`).join(' | ') : 'none yet'}
${day.notes ? `Notes: ${day.notes}` : ''}

RECENT DAYS:
${recent || 'no prior days logged'}

HOURS — CURRENT BILLING PERIOD (${cur.label}, actual/target): ${cur.summary}
ITG sub-clients this period:
${cur.itgLines}
Clarys clients this period:
${cur.clarysLines}

HOURS — PREVIOUS BILLING PERIOD (${prev.label}, actual/target): ${prev.summary}
ITG sub-clients last period:
${prev.itgLines}
Clarys clients last period:
${prev.clarysLines}

If Johnny asks you to summarize hours for an invoice, draft a clean "Invoice for <client>, period <label>" with daily line items from the data above. Dusty is the only ITG sub-client; ITG totals above equal the Dusty total.

STYLE:
- Direct, concise, no caveats. He already knows you're not a lawyer or financial advisor.
- Challenge assumptions. Push back when useful.
- Daily check-ins should be short. Weekly reviews can go deeper.
- When suggesting action items, format each as a markdown list item with an ISO date in brackets: "- [YYYY-MM-DD] Description". For multi-day items use "- [YYYY-MM-DD..YYYY-MM-DD] Description". The bracket lets the app one-click add the item to the right day(s). Do not add the date again in the description. If an item belongs on today, still include today's date in brackets.
- When Johnny tells you he worked hours, emit one or more naked "[HOURS: YYYY-MM-DD STREAM AMOUNT]" tokens anywhere in your reply (they don't need to be in a list). Use "[HOURS: YYYY-MM-DD Clarys:<ClientName> AMOUNT]" for Clarys client work. Known streams: ITG, Dusty (ITG sub-client), Clarys, Clarys:<client>. AMOUNT is hours (halves ok: 0.5, 1.5, 3). The app will render an "Apply" button next to each token so Johnny can one-click log. These tokens mean "ADD these hours" (not "set to"). Example reply: "Logged. [HOURS: 2026-04-24 ITG 3] [HOURS: 2026-04-24 Clarys:Fred 2]"
- Be strategic, not motivational. He doesn't need cheerleading.
- Address him as "you". Don't use his name repeatedly.

STRATEGIC CONTEXT (living doc — Johnny edits as plans shift):
${strategyMd}`;
}

const MONTH_MAP = {
  jan:0, january:0, feb:1, february:1, mar:2, march:2, apr:3, april:3,
  may:4, jun:5, june:5, jul:6, july:6, aug:7, august:7,
  sep:8, sept:8, september:8, oct:9, october:9, nov:10, november:10, dec:11, december:11,
};
const MONTH_KEYS = Object.keys(MONTH_MAP).join('|');
const DAY_NAME_PAT = 'mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday';

function datesBetween(start, end) {
  const out = [];
  const cur = new Date(start); cur.setHours(0, 0, 0, 0);
  const lim = new Date(end); lim.setHours(0, 0, 0, 0);
  let safety = 60;
  while (cur <= lim && safety-- > 0) {
    out.push(fmtDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function parseItemDates(raw, ctxDate) {
  const iso = raw.match(/^\[(\d{4}-\d{2}-\d{2})(?:\.\.(\d{4}-\d{2}-\d{2}))?\]\s*[-—–:]?\s*(.+)$/);
  if (iso) {
    const start = parseDate(iso[1]);
    const end = iso[2] ? parseDate(iso[2]) : start;
    return { text: iso[3].trim(), dates: datesBetween(start, end) };
  }

  const re = new RegExp(
    `^(?:(?:${DAY_NAME_PAT})\\s+)?` +
    `(${MONTH_KEYS})\\s+(\\d{1,2})` +
    `(?:\\s*[–—-]\\s*(?:(?:${DAY_NAME_PAT})\\s+)?(?:(${MONTH_KEYS})\\s+)?(\\d{1,2}))?` +
    `(?:,?\\s*(\\d{1,2}:\\d{2}))?` +
    `\\s*[—–-]\\s*(.+)$`,
    'i'
  );
  const m = raw.match(re);
  if (m) {
    const [, mon1, d1, mon2, d2, time, rest] = m;
    const year = ctxDate.getFullYear();
    const mIdx1 = MONTH_MAP[mon1.toLowerCase()];
    const start = new Date(year, mIdx1, parseInt(d1, 10));
    if ((start - ctxDate) / (86400000 * 30) < -6) start.setFullYear(year + 1);
    let end = start;
    if (d2) {
      const mIdx2 = mon2 ? MONTH_MAP[mon2.toLowerCase()] : start.getMonth();
      end = new Date(start.getFullYear(), mIdx2, parseInt(d2, 10));
      if (end < start) end.setFullYear(end.getFullYear() + 1);
    }
    const body = time ? `${time} ${rest.trim()}` : rest.trim();
    return { text: body, dates: datesBetween(start, end) };
  }

  return { text: raw, dates: [] };
}

function extractListItems(text, contextDateKey) {
  const ctxDate = contextDateKey ? parseDate(contextDateKey) : new Date();
  const lines = text.split('\n');
  const items = [];
  for (const line of lines) {
    const m = line.match(/^\s*[-*]\s+(.+?)$/) || line.match(/^\s*\d+[.)]\s+(.+?)$/);
    if (!m) continue;
    const raw = m[1].replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').trim();
    if (raw.length <= 3 || raw.length >= 200) continue;
    if (raw.startsWith('[HOURS:')) continue;
    const parsed = parseItemDates(raw, ctxDate);
    if (parsed.text.length <= 1) continue;
    items.push(parsed);
  }
  return items;
}

function extractHoursItems(text) {
  const re = /\[HOURS:\s*(\d{4}-\d{2}-\d{2})\s+([A-Za-z]+)(?::\s*([^\]]+?))?\s+(\d+(?:\.\d+)?)\s*\]/g;
  const items = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    items.push({ date: m[1], stream: m[2], client: m[3]?.trim() || null, amount: parseFloat(m[4]) });
  }
  return items;
}

function formatDateBadge(dates) {
  if (!dates || !dates.length) return '';
  const first = parseDate(dates[0]);
  if (dates.length === 1) return `${MONTHS[first.getMonth()]} ${first.getDate()}`;
  const last = parseDate(dates[dates.length - 1]);
  if (first.getMonth() === last.getMonth()) return `${MONTHS[first.getMonth()]} ${first.getDate()}–${last.getDate()}`;
  return `${MONTHS[first.getMonth()]} ${first.getDate()} – ${MONTHS[last.getMonth()]} ${last.getDate()}`;
}

// ---------- Component ----------
export default function Operator() {
  const [state, setState] = useState(() => loadLocal());
  const [view, setView] = useState('week');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const [anchor, setAnchor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(fmtDate(new Date()));
  const [periodAnchor, setPeriodAnchor] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [editingGoals, setEditingGoals] = useState(false);
  const [mobilePanel, setMobilePanel] = useState('cal');
  const [enableDrag, setEnableDrag] = useState(() =>
    typeof window !== 'undefined' && !window.matchMedia('(pointer: coarse)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setEnableDrag(!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  // sync: 'loading' on mount, 'saved' when in sync, 'saving' during push, 'error' on failure, 'offline' when fetch fails
  const [syncStatus, setSyncStatus] = useState('loading');
  const chatEndRef = useRef(null);
  const hydratedRef = useRef(false);       // gate writes until initial remote load completes
  const pushTimerRef = useRef(null);
  const lastPushedRef = useRef('');
  const panelHistoryRef = useRef(false);

  const today = useMemo(() => new Date(), []);
  const todayKey = fmtDate(today);
  const currentTheme = THEMES[state.theme] || THEMES.blue;
  const setTheme = (key) => setState(s => ({ ...s, theme: key }));

  useEffect(() => {
    if (!settingsOpen) return;
    const onDown = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settingsOpen]);

  // On mount: hydrate from remote. Remote wins if it has real data, else push local seed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { state: remote } = await fetchRemote();
        if (cancelled) return;
        if (hasRealData(remote)) {
          const merged = { ...DEFAULT_STATE, ...remote };
          merged.quarters = DEFAULT_STATE.quarters;
          merged.hoursTargets = DEFAULT_STATE.hoursTargets;
          merged.notes = normalizeNotes(merged.notes);
          merged.chats = normalizeChat(merged.chats);
          setState(merged);
          saveLocal(merged);
          lastPushedRef.current = JSON.stringify(merged);
        } else {
          // Remote empty → seed it with whatever we had locally (or DEFAULT_STATE).
          const local = loadLocal();
          await pushRemote(local);
          lastPushedRef.current = JSON.stringify(local);
        }
        setSyncStatus('saved');
      } catch (e) {
        console.warn('remote hydrate failed, using local cache', e);
        setSyncStatus('offline');
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // On state change: always cache locally; debounced push to remote (after hydrate).
  useEffect(() => {
    saveLocal(state);
    if (!hydratedRef.current) return;

    const serialized = JSON.stringify(state);
    if (serialized === lastPushedRef.current) return;

    setSyncStatus(s => (s === 'offline' ? 'offline' : 'saving'));
    clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(async () => {
      try {
        await pushRemote(state);
        lastPushedRef.current = serialized;
        setSyncStatus('saved');
      } catch (e) {
        console.warn('remote push failed', e);
        setSyncStatus('error');
      }
    }, 1200);

    return () => clearTimeout(pushTimerRef.current);
  }, [state]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [state.chats, selectedDay]);

  const updateDay = (key, updater) => {
    setState(s => {
      const prev = s.days[key] || { items: [], notes: '', hours: {} };
      return { ...s, days: { ...s.days, [key]: updater(prev) } };
    });
  };

  const addItem = (key, text, stream = null) => {
    if (!text.trim()) return;
    updateDay(key, d => ({ ...d, items: [...(d.items || []), { id: Date.now() + Math.random(), text: text.trim(), done: false, stream }] }));
  };
  const toggleItem = (key, id) => {
    updateDay(key, d => ({ ...d, items: d.items.map(i => i.id === id ? { ...i, done: !i.done } : i) }));
  };
  const deleteItem = (key, id) => {
    updateDay(key, d => ({ ...d, items: d.items.filter(i => i.id !== id) }));
  };
  const editItem = (key, id, text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    updateDay(key, d => ({
      ...d,
      items: (d.items || []).map(i => i.id === id ? { ...i, text: trimmed } : i),
    }));
  };
  const reorderItems = (key, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    updateDay(key, d => {
      const items = [...(d.items || [])];
      if (fromIndex < 0 || fromIndex >= items.length) return d;
      const [moved] = items.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, items.length));
      items.splice(insertAt, 0, moved);
      return { ...d, items };
    });
  };
  const moveItem = (fromKey, toKey, id) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setState(s => {
      const fromDay = s.days[fromKey] || { items: [] };
      const item = (fromDay.items || []).find(i => i.id === id);
      if (!item) return s;
      const toDay = s.days[toKey] || { items: [], notes: '', hours: {} };
      return {
        ...s,
        days: {
          ...s.days,
          [fromKey]: { ...fromDay, items: (fromDay.items || []).filter(i => i.id !== id) },
          [toKey]: { ...toDay, items: [...(toDay.items || []), item] },
        },
      };
    });
  };
  const setNotes = (key, notes) => updateDay(key, d => ({ ...d, notes }));

  const generateITGInvoice = async ({ periodKey, dougHrs, dustyHrs, invoiceNo, invoiceDate }) => {
    await generateITGInvoiceDocx({ periodKey, dougHrs, dustyHrs, invoiceNo, invoiceDate });
    setState(s => ({
      ...s,
      invoiceCounters: { ...(s.invoiceCounters || {}), ITG: invoiceNo + 1 },
    }));
  };

  const updateGoals = (patch) => setState(s => ({ ...s, goals: { ...s.goals, ...patch } }));
  const updateNotes = (fn) => setState(s => ({ ...s, notes: fn(Array.isArray(s.notes) ? s.notes : []) }));
  const clearNotes = () => {
    if (!window.confirm('Clear all notes?')) return;
    setState(s => ({ ...s, notes: [] }));
  };
  const clearChats = () => {
    if (!window.confirm('Clear chat thread?')) return;
    setState(s => ({ ...s, chats: [] }));
  };

  const setDayHours = (dateKey, stream, value) => {
    updateDay(dateKey, d => ({ ...d, hours: { ...(d.hours || {}), [stream]: value } }));
  };
  const updateClarysPeriod = (pKey, updater) => {
    setState(s => {
      const prev = (s.clarysPeriods || {})[pKey] || [];
      return { ...s, clarysPeriods: { ...(s.clarysPeriods || {}), [pKey]: updater(prev) } };
    });
  };
  const addClarysRow = (pKey) => updateClarysPeriod(pKey, rows => [...rows, { id: Date.now() + Math.random(), client: '', dayHours: {} }]);
  const setClarysClient = (pKey, id, client) => updateClarysPeriod(pKey, rows => rows.map(r => r.id === id ? { ...r, client } : r));
  const setClarysCell = (pKey, id, dateKey, hours) => updateClarysPeriod(pKey, rows => rows.map(r => r.id === id ? { ...r, dayHours: { ...(r.dayHours || {}), [dateKey]: hours } } : r));
  const deleteClarysRow = (pKey, id) => updateClarysPeriod(pKey, rows => rows.filter(r => r.id !== id));

  const STREAM_ALIASES = { itg: 'ITG', dusty: 'Dusty', clarys: 'Clarys', coop: 'Coop' };
  const applyHours = ({ date, stream, client, amount }) => {
    if (!amount) return;
    if (stream.toLowerCase() === 'clarys' && client) {
      const pk = periodKey(parseDate(date));
      setState(s => {
        const periods = s.clarysPeriods || {};
        const rows = periods[pk] || [];
        const existing = rows.find(r => r.client?.trim().toLowerCase() === client.toLowerCase());
        const nextRows = existing
          ? rows.map(r => r.id === existing.id
              ? { ...r, dayHours: { ...(r.dayHours || {}), [date]: (r.dayHours?.[date] || 0) + amount } }
              : r)
          : [...rows, { id: Date.now() + Math.random(), client, dayHours: { [date]: amount } }];
        return { ...s, clarysPeriods: { ...periods, [pk]: nextRows } };
      });
      return;
    }
    const canonical = STREAM_ALIASES[stream.toLowerCase()] || stream;
    updateDay(date, d => ({ ...d, hours: { ...(d.hours || {}), [canonical]: (d.hours?.[canonical] || 0) + amount } }));
  };

  const sendMessage = async () => {
    const txt = chatInput.trim();
    if (!txt || chatLoading) return;
    setChatError('');
    const now = Date.now();
    const userMsg = { role: 'user', content: txt, ts: now, date: selectedDay };
    const nextChats = [...(state.chats || []), userMsg];
    setState(s => ({ ...s, chats: nextChats }));
    setChatInput('');
    setChatLoading(true);
    try {
      const WINDOW = 40;
      const windowed = nextChats.slice(-WINDOW);
      const messagesForAPI = windowed.map(m => ({
        role: m.role,
        content: m.date && m.date !== selectedDay ? `[${m.date}] ${m.content}` : m.content,
      }));
      const systemPrompt = buildSystemPrompt(state, parseDate(selectedDay));
      const reply = await callClaude(systemPrompt, messagesForAPI);
      const asst = { role: 'assistant', content: reply, ts: Date.now(), date: selectedDay };
      setState(s => ({ ...s, chats: [...(s.chats || []), asst] }));
    } catch (e) {
      setChatError(e.message || 'Something went wrong.');
    } finally {
      setChatLoading(false);
    }
  };

  const navigatePrev = () => {
    if (view === 'week') setAnchor(addDays(anchor, -7));
    else if (view === 'month') setAnchor(addMonths(anchor, -1));
    else setAnchor(addMonths(anchor, -12));
  };
  const navigateNext = () => {
    if (view === 'week') setAnchor(addDays(anchor, 7));
    else if (view === 'month') setAnchor(addMonths(anchor, 1));
    else setAnchor(addMonths(anchor, 12));
  };
  const jumpToday = () => { setAnchor(new Date()); setSelectedDay(todayKey); setMobilePanel('today'); };
  const selectDay = (key) => {
    setSelectedDay(key);
    setMobilePanel('today');
    if (!panelHistoryRef.current && typeof window !== 'undefined') {
      try { window.history.pushState({ opPanel: true }, ''); panelHistoryRef.current = true; } catch {}
    }
  };
  const closeDayPanel = () => {
    if (panelHistoryRef.current && typeof window !== 'undefined') {
      try { window.history.back(); return; } catch {}
    }
    setMobilePanel('cal');
  };

  useEffect(() => {
    const onPop = () => {
      if (panelHistoryRef.current) {
        panelHistoryRef.current = false;
        setMobilePanel('cal');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const viewLabel = useMemo(() => {
    if (view === 'week') {
      const s = addDays(anchor, -1); const e = addDays(s, 13);
      return `Weeks ${weekNum(s)}–${weekNum(e)} · ${MONTHS[s.getMonth()]} ${s.getDate()} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
    }
    if (view === 'month') return `${MONTHS_FULL[anchor.getMonth()]} ${anchor.getFullYear()}`;
    return `${anchor.getFullYear()}`;
  }, [view, anchor]);

  return (
    <div className="min-h-screen text-stone-900" style={{
      '--theme-body': currentTheme.body,
      '--theme-tint': currentTheme.tint,
      '--theme-soft-tint': currentTheme.softTint,
      '--theme-bar': currentTheme.bar,
      '--theme-tint-alpha': currentTheme.tintAlpha,
      '--theme-accent': currentTheme.accent,
      '--theme-strong': currentTheme.strong,
      backgroundColor: 'var(--theme-body)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
    }}>
      <style>{`
        :root { --accent: #1a3a2e; --accent-soft: #e7ede9; }
        .serif { font-family: "Iowan Old Style", "Palatino Linotype", Palatino, "URW Palladio L", Georgia, serif; font-feature-settings: "onum" 1; }
        .scrollbar-thin::-webkit-scrollbar { width: 6px; height: 6px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 3px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .fade-in { animation: fade 0.3s ease; }
        @keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .today-col { background-color: var(--theme-soft-tint); border-color: var(--theme-strong); }
        .theme-bar { background-color: var(--theme-bar); }
        .theme-strong { color: var(--theme-strong); }
        .theme-hover:hover { color: var(--theme-strong); }
        .theme-ring:focus { --tw-ring-color: var(--theme-strong); }
      `}</style>

      <header className="border-b border-stone-200 backdrop-blur sticky top-0 z-20" style={{ backgroundColor: 'var(--theme-tint-alpha)', paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-[1600px] 2xl:max-w-[1900px] mx-auto px-4 lg:px-8 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-900 text-white flex items-center justify-center flex-shrink-0">
              <Target size={16} />
            </div>
            <div className="min-w-0">
              <h1 className="serif text-xl lg:text-2xl leading-none tracking-tight">Operator</h1>
              <p className="text-xs text-stone-500 mt-0.5 truncate">{state.goals.northStar}</p>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 bg-stone-100 rounded-full p-1">
            {['week', 'month', 'year'].map(v => (
              <button key={v}
                onClick={() => setView(v)}
                style={view === v ? { backgroundColor: 'var(--theme-accent)' } : undefined}
                className={`text-sm px-4 py-1.5 rounded-full capitalize transition ${view === v ? 'shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-900'}`}>
                {v}
              </button>
            ))}
          </nav>

          <button onClick={jumpToday} className="hidden md:block text-sm text-stone-500 hover:text-stone-900 px-3 py-1.5 rounded-full">
            Today
          </button>

          <FullscreenButton />

          <div className="relative" ref={settingsRef}>
            <button
              onClick={() => setSettingsOpen(o => !o)}
              className="p-1.5 rounded-full text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition"
              title="Settings"
            >
              <Settings size={16} />
            </button>
            {settingsOpen && (
              <div className="fixed md:absolute right-2 md:right-0 top-[calc(env(safe-area-inset-top)+3.25rem)] md:top-auto md:mt-2 w-44 max-w-[calc(100vw-1rem)] bg-white border border-stone-200 rounded-lg shadow-lg p-2 z-30">
                <div className="text-[10px] uppercase tracking-wider text-stone-400 px-2 pt-1 pb-1.5">Theme</div>
                {Object.entries(THEMES).map(([key, t]) => {
                  const isActive = state.theme === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setTheme(key); setSettingsOpen(false); }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-stone-100 transition ${isActive ? 'text-stone-900 font-medium' : 'text-stone-600'}`}
                    >
                      <span className="w-5 h-5 rounded border border-stone-300 flex-shrink-0" style={{ backgroundColor: t.tint }} />
                      <span className="flex-1 text-left">{t.name}</span>
                      {isActive && <Check size={14} className="text-stone-700" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <SyncBadge status={syncStatus} />

          <div className="md:hidden flex items-center gap-1">
            <button onClick={() => setMobilePanel('goals')} style={mobilePanel==='goals' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><Target size={18}/></button>
            <button onClick={closeDayPanel} style={mobilePanel==='cal' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><CalIcon size={18}/></button>
            <button onClick={() => setMobilePanel('today')} style={mobilePanel==='today' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><Check size={18}/></button>
            <button onClick={() => setMobilePanel('notes')} style={mobilePanel==='notes' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><StickyNote size={18}/></button>
            <button onClick={() => setMobilePanel('chat')} style={mobilePanel==='chat' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><MessageSquare size={18}/></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] 2xl:max-w-[1900px] mx-auto px-4 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr_380px] gap-y-6 gap-x-14">

          <aside className={`space-y-5 ${mobilePanel==='goals'?'':'hidden'} md:block`}>
            <Section title="North Star" icon={<Target size={14} />}>
              {editingGoals ? (
                <textarea
                  value={state.goals.northStar}
                  onChange={e => updateGoals({ northStar: e.target.value })}
                  className="w-full text-base p-2 border border-stone-200 rounded bg-white resize-none"
                  rows={3}
                />
              ) : (
                <p className="text-base text-stone-700 leading-relaxed">{state.goals.northStar}</p>
              )}
            </Section>

            <Section title={`${today.getFullYear()} Goal`}>
              {editingGoals ? (
                <textarea
                  value={state.goals.yearly[today.getFullYear()] || ''}
                  onChange={e => updateGoals({ yearly: { ...state.goals.yearly, [today.getFullYear()]: e.target.value } })}
                  className="w-full text-base p-2 border border-stone-200 rounded bg-white resize-none"
                  rows={3}
                />
              ) : (
                <p className="text-base text-stone-700 leading-relaxed">{state.goals.yearly[today.getFullYear()]}</p>
              )}
            </Section>

            <NotesList
              notes={state.notes || []}
              updateNotes={updateNotes}
              clearAll={clearNotes}
              compact
            />


            <button
              onClick={() => setEditingGoals(!editingGoals)}
              className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1.5"
            >
              <Edit2 size={12} /> {editingGoals ? 'Done' : 'Edit goals'}
            </button>
          </aside>

          <main className={`${mobilePanel==='cal'?'':'hidden'} md:block`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button onClick={navigatePrev} className="p-1.5 hover:bg-stone-100 rounded"><ChevronLeft size={18} /></button>
                <h2 className="serif text-2xl tracking-tight">{viewLabel}</h2>
                <button onClick={navigateNext} className="p-1.5 hover:bg-stone-100 rounded"><ChevronRight size={18} /></button>
              </div>
              <div className="md:hidden">
                <select value={view} onChange={e=>setView(e.target.value)} className="text-sm bg-stone-100 rounded-full px-3 py-1.5 border-0 capitalize">
                  <option value="week">week</option>
                  <option value="month">month</option>
                  <option value="year">year</option>
                </select>
              </div>
            </div>

            {view === 'week' && <WeekView anchor={anchor} today={today} state={state} selectedDay={selectedDay} onSelectDay={selectDay} toggleItem={toggleItem} moveItem={moveItem} enableDrag={enableDrag} setDayHours={setDayHours} addClarysRow={addClarysRow} setClarysClient={setClarysClient} setClarysCell={setClarysCell} deleteClarysRow={deleteClarysRow} periodAnchor={periodAnchor} setPeriodAnchor={setPeriodAnchor} generateITGInvoice={generateITGInvoice} />}
            {view === 'month' && <MonthView anchor={anchor} today={today} state={state} selectedDay={selectedDay} onSelectDay={selectDay} setAnchor={setAnchor} setView={setView} />}
            {view === 'year' && <YearView anchor={anchor} today={today} state={state} setAnchor={setAnchor} setView={setView} />}
          </main>

          <aside className={`space-y-4 ${['today','chat','notes'].includes(mobilePanel)?'':'hidden'} md:block`}>
            <div className={mobilePanel==='chat' || mobilePanel==='notes' ? 'hidden md:block' : ''}>
              <TodayPanel
                dayKey={selectedDay}
                day={state.days[selectedDay]}
                isToday={selectedDay === todayKey}
                addItem={addItem}
                toggleItem={toggleItem}
                deleteItem={deleteItem}
                editItem={editItem}
                reorderItems={reorderItems}
                enableDrag={enableDrag}
                setNotes={setNotes}
                setSelectedDay={setSelectedDay}
                setAnchor={setAnchor}
                today={today}
              />
            </div>

            <div className={mobilePanel==='today' || mobilePanel==='notes' ? 'hidden md:block' : ''}>
              <ChatPanel
                dayKey={selectedDay}
                messages={Array.isArray(state.chats) ? state.chats : []}
                input={chatInput}
                setInput={setChatInput}
                send={sendMessage}
                loading={chatLoading}
                error={chatError}
                onAddItem={(text, dates) => {
                  const keys = dates && dates.length ? dates : [selectedDay];
                  keys.forEach(k => addItem(k, text));
                }}
                onApplyHours={applyHours}
                onClear={clearChats}
                chatEndRef={chatEndRef}
                mobileOnly={mobilePanel==='chat'}
              />
            </div>

            {mobilePanel==='notes' && (
              <div className="md:hidden">
                <NotesList
                  notes={state.notes || []}
                  updateNotes={updateNotes}
                  clearAll={clearNotes}
                />
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function FullscreenButton() {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const handler = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };
  const Icon = isFull ? Minimize2 : Maximize2;
  return (
    <button
      onClick={toggle}
      title={isFull ? 'Exit fullscreen' : 'Fullscreen'}
      className="p-1.5 text-stone-400 hover:text-stone-900 rounded"
    >
      <Icon size={16} />
    </button>
  );
}

function SyncBadge({ status }) {
  const config = {
    loading: { dot: 'bg-stone-300 animate-pulse', label: 'Loading' },
    saving:  { dot: 'bg-amber-400 animate-pulse',  label: 'Saving' },
    saved:   { dot: 'bg-emerald-600',              label: 'Synced' },
    error:   { dot: 'bg-red-500',                  label: 'Sync error' },
    offline: { dot: 'bg-stone-400',                label: 'Offline' },
  }[status] || { dot: 'bg-stone-300', label: '' };
  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-stone-500" title={config.label}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      <span>{config.label}</span>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col space-y-2 fade-in">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium" style={{ color: '#E8963A' }}>
        {icon}{title}
      </div>
      {children}
    </div>
  );
}

function WeekView({ anchor, today, state, selectedDay, onSelectDay, toggleItem, moveItem, enableDrag, setDayHours, addClarysRow, setClarysClient, setClarysCell, deleteClarysRow, periodAnchor, setPeriodAnchor, generateITGInvoice }) {
  const start = addDays(anchor, -1);
  const days = Array.from({ length: 14 }, (_, i) => addDays(start, i));
  const [dragOverKey, setDragOverKey] = useState(null);
  const [touchDragId, setTouchDragId] = useState(null);
  const dragOverKeyRef = useRef(null);
  const touchDragRef = useRef(null);
  const longPressRef = useRef(null);
  const touchStartRef = useRef(null);
  const movedRef = useRef(false);
  const justActivatedRef = useRef(false);

  useEffect(() => { dragOverKeyRef.current = dragOverKey; }, [dragOverKey]);

  useEffect(() => {
    if (!touchDragId) return;
    const onMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const dayEl = el?.closest?.('[data-day-key]');
      const k = dayEl?.dataset?.dayKey || null;
      if (k !== dragOverKeyRef.current) setDragOverKey(k);
    };
    const onEnd = () => {
      const overKey = dragOverKeyRef.current;
      const drag = touchDragRef.current;
      if (drag && overKey && overKey !== drag.fromKey) {
        moveItem(drag.fromKey, overKey, drag.id);
      }
      touchDragRef.current = null;
      setDragOverKey(null);
      setTouchDragId(null);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [touchDragId, moveItem]);

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d) => {
          const key = fmtDate(d);
          const day = state.days[key] || { items: [] };
          const isToday = sameDay(d, today);
          const isSelected = key === selectedDay;
          const doneCount = day.items.filter(i => i.done).length;
          const dayName = DAYS_SHORT[(d.getDay() + 6) % 7];
          const isDropTarget = (enableDrag || touchDragId) && dragOverKey === key;
          return (
            <button
              key={key}
              data-day-key={key}
              onClick={() => onSelectDay(key)}
              onDoubleClick={() => onSelectDay(key)}
              onDragOver={enableDrag ? (e) => {
                if (!e.dataTransfer.types.includes('application/x-operator-item')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverKey !== key) setDragOverKey(key);
              } : undefined}
              onDragLeave={enableDrag ? (e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (dragOverKey === key) setDragOverKey(null);
              } : undefined}
              onDrop={enableDrag ? (e) => {
                e.preventDefault();
                setDragOverKey(null);
                try {
                  const raw = e.dataTransfer.getData('application/x-operator-item');
                  if (!raw) return;
                  const data = JSON.parse(raw);
                  if (data && data.fromKey != null && data.id != null) moveItem(data.fromKey, key, data.id);
                } catch {}
              } : undefined}
              style={isSelected ? { backgroundColor: 'var(--theme-tint)' } : undefined}
              className={`text-left p-3 rounded-lg border transition h-[210px] flex flex-col ${
                isDropTarget ? 'border-emerald-700 ring-2 ring-emerald-600/40 bg-white' :
                isSelected ? 'border-emerald-900 shadow-sm' :
                isToday ? 'border-stone-300 bg-white' :
                'border-stone-200 bg-white/50 hover:bg-white'
              }`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-400">{dayName}</div>
                  <div className={`serif text-xl leading-none ${isToday ? 'text-emerald-900' : ''}`}>{d.getDate()}</div>
                </div>
                {day.items.length > 0 && (
                  <div className="text-[10px] text-stone-400 tabular-nums">{doneCount}/{day.items.length}</div>
                )}
              </div>
              <ul className="space-y-1 flex-1 overflow-hidden">
                {day.items.slice(0, 4).map(item => (
                  <li
                    key={item.id}
                    draggable={enableDrag}
                    className="select-none"
                    style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                    onDragStart={enableDrag ? (e) => {
                      e.stopPropagation();
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('application/x-operator-item', JSON.stringify({ fromKey: key, id: item.id }));
                    } : undefined}
                    onDragEnd={enableDrag ? () => { setDragOverKey(null); } : undefined}
                    onTouchStart={(e) => {
                      if (touchDragRef.current) return;
                      const t = e.touches[0];
                      touchStartRef.current = { x: t.clientX, y: t.clientY };
                      movedRef.current = false;
                      justActivatedRef.current = false;
                      cancelLongPress();
                      const fromKey = key;
                      const id = item.id;
                      longPressRef.current = setTimeout(() => {
                        longPressRef.current = null;
                        if (movedRef.current) return;
                        if (navigator.vibrate) navigator.vibrate(10);
                        justActivatedRef.current = true;
                        touchDragRef.current = { id, fromKey };
                        setTouchDragId(id);
                      }, 400);
                    }}
                    onTouchMove={(e) => {
                      if (touchDragRef.current) return;
                      const t = e.touches[0];
                      if (!t || !touchStartRef.current) return;
                      const dx = Math.abs(t.clientX - touchStartRef.current.x);
                      const dy = Math.abs(t.clientY - touchStartRef.current.y);
                      if (dx > 8 || dy > 8) {
                        movedRef.current = true;
                        cancelLongPress();
                      }
                    }}
                    onTouchEnd={cancelLongPress}
                    onTouchCancel={cancelLongPress}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (justActivatedRef.current) {
                        justActivatedRef.current = false;
                        return;
                      }
                      toggleItem(key, item.id);
                    }}
                    className={`text-xs leading-snug flex gap-1.5 ${item.done ? 'text-stone-400 line-through' : 'text-stone-700'} ${enableDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${touchDragId === item.id ? 'font-medium scale-[1.03]' : ''} transition-transform`}
                  >
                    <span className={`flex-shrink-0 mt-0.5 w-3 h-3 rounded border ${item.done ? 'bg-emerald-800 border-emerald-800' : 'border-stone-300'} flex items-center justify-center`}>
                      {item.done && <Check size={9} className="text-white" />}
                    </span>
                    <span className="truncate">{item.text}</span>
                  </li>
                ))}
                {day.items.length > 4 && (
                  <li className="text-[10px] text-stone-400">+{day.items.length - 4} more</li>
                )}
              </ul>
            </button>
          );
        })}
      </div>
      <HoursGrid
        today={today}
        periodAnchor={periodAnchor}
        setPeriodAnchor={setPeriodAnchor}
        state={state}
        targets={state.hoursTargets}
        setDayHours={setDayHours}
        addClarysRow={addClarysRow}
        setClarysClient={setClarysClient}
        setClarysCell={setClarysCell}
        deleteClarysRow={deleteClarysRow}
        generateITGInvoice={generateITGInvoice}
      />
    </div>
  );
}

const ITG_SUBS = ['Dusty'];

function HourInput({ value, onCommit }) {
  const display = value === 0 || value == null || value === '' ? '' : String(value);
  const [local, setLocal] = useState(display);
  useEffect(() => { setLocal(display); }, [display]);
  return (
    <input
      type="number"
      step="0.5"
      min="0"
      max="24"
      inputMode="decimal"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        const parsed = local === '' ? 0 : parseFloat(local);
        onCommit(Number.isFinite(parsed) ? parsed : 0);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      placeholder="–"
      className="hour-input w-full text-center tabular-nums text-xs bg-transparent focus:bg-white focus:outline-none focus:ring-1 theme-ring rounded px-0.5 py-1 placeholder:text-stone-300"
    />
  );
}

function TargetDot({ actual, target }) {
  if (!target) return null;
  const pct = actual / target;
  const color = pct >= 0.9 ? 'bg-emerald-600' : pct >= 0.6 ? 'bg-amber-500' : 'bg-red-400';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color} align-middle ml-1.5`} />;
}

function HoursGrid({ today, periodAnchor, setPeriodAnchor, state, targets, setDayHours, addClarysRow, setClarysClient, setClarysCell, deleteClarysRow, generateITGInvoice }) {
  const anchorDate = periodAnchor || today;
  const days = periodDays(anchorDate);
  const pKey = periodKey(anchorDate);
  const pStart = periodStart(anchorDate);
  const pEnd = periodEnd(anchorDate);
  const isCurrentPeriod = pKey === periodKey(today);
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const isPastPeriod = pEnd < todayMidnight;
  const [invoiceModal, setInvoiceModal] = useState(null);
  const prevAnchor = new Date(pStart.getFullYear(), pStart.getMonth(), pStart.getDate() - 1);
  const nextAnchor = new Date(pEnd.getFullYear(), pEnd.getMonth(), pEnd.getDate() + 1);
  const prevLabel = `${MONTHS[periodStart(prevAnchor).getMonth()]} ${periodStart(prevAnchor).getDate()}–${periodEnd(prevAnchor).getDate()}`;
  const nextLabel = `${MONTHS[periodStart(nextAnchor).getMonth()]} ${periodStart(nextAnchor).getDate()}–${periodEnd(nextAnchor).getDate()}`;
  const clarysRows = (state.clarysPeriods || {})[pKey] || [];

  const scale = days.length / 7;
  const periodTarget = (w) => Math.round(w * scale * 2) / 2;

  const scalarTotal = (stream) => days.reduce((sum, d) => {
    const h = state.days[fmtDate(d)]?.hours?.[stream];
    return sum + (typeof h === 'number' ? h : 0);
  }, 0);
  const clarysRowTotal = (row) => days.reduce((sum, d) => sum + (row.dayHours?.[fmtDate(d)] || 0), 0);
  const clarysPeriodTotal = clarysRows.reduce((sum, r) => sum + clarysRowTotal(r), 0);
  const itgRollup = scalarTotal('ITG') + ITG_SUBS.reduce((s, sub) => s + scalarTotal(sub), 0);
  const clarysRollup = scalarTotal('Clarys') + clarysPeriodTotal;
  const grandTotal = itgRollup + clarysRollup;

  const todayKey = fmtDate(today);
  const todayIdx = days.findIndex(d => fmtDate(d) === todayKey);
  const colClass = 'px-1 py-1 text-center';
  const lblClass = 'text-[10px] uppercase tracking-wider text-stone-500';
  const pLabel = `${MONTHS[pStart.getMonth()]} ${pStart.getDate()}–${pEnd.getDate()}`;
  const nCols = days.length + 3;
  const todayBoxBase = 'today-col border-l-2 border-r-2';
  const todayCls = (i, pos) => {
    if (i !== todayIdx) return '';
    if (pos === 'header') return `${todayBoxBase} border-t-2`;
    if (pos === 'last') return `${todayBoxBase} border-b-2`;
    return todayBoxBase;
  };

  return (
    <div className="mt-4 border border-stone-200 rounded-lg bg-white/70 overflow-x-auto">
      <div className="px-3 py-2 flex items-baseline justify-between border-b border-stone-100 theme-bar">
        <div className="flex items-baseline gap-2">
          <span className={`${lblClass} font-medium`}>Billing period</span>
          <span className="serif text-base text-stone-700">{pLabel}</span>
        </div>
        <span className="text-[10px] text-stone-400 tabular-nums">{days.length} days</span>
      </div>
      <table className="hours-grid w-full text-xs" style={{ minWidth: `${120 + days.length * 54 + 140}px` }}>
        <thead>
          <tr className="border-b border-stone-200 theme-bar">
            <th className={`text-left px-2 py-1.5 ${lblClass}`}>Stream</th>
            {days.map((d, i) => {
              const isToday = i === todayIdx;
              return (
                <th key={i} className={`${colClass} font-normal ${lblClass} tabular-nums ${isToday ? 'theme-strong' : ''} ${todayCls(i, 'header')}`}>
                  <div>{DAYS_SHORT[(d.getDay() + 6) % 7]}</div>
                  <div className={isToday ? 'theme-strong font-medium' : 'text-stone-300'}>{d.getDate()}</div>
                </th>
              );
            })}
            <th className={`text-right px-2 py-1.5 ${lblClass}`}>Period</th>
            <th className={`text-right px-2 py-1.5 ${lblClass}`}>Target</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-stone-100">
            <td className="px-2 py-1 font-medium text-stone-700">
              <span className="inline-flex items-center gap-1.5">
                ITG
                {isPastPeriod && itgRollup > 0 && generateITGInvoice && (
                  <button
                    type="button"
                    onClick={() => setInvoiceModal({
                      periodKey: pKey,
                      dougHrs: scalarTotal('ITG'),
                      dustyHrs: scalarTotal('Dusty'),
                      invoiceNo: state.invoiceCounters?.ITG || 2606,
                      invoiceDate: new Date(today),
                    })}
                    title="Generate ITG invoice for this period"
                    className="text-stone-400 hover:text-emerald-800 transition"
                  >
                    <FileText size={13} />
                  </button>
                )}
              </span>
            </td>
            {days.map((d, i) => {
              const dk = fmtDate(d);
              const v = state.days[dk]?.hours?.ITG || 0;
              return (
                <td key={i} className={`px-1 py-1 text-center ${todayCls(i)}`}>
                  <HourInput value={v} onCommit={nv => setDayHours(dk, 'ITG', nv)} />
                </td>
              );
            })}
            <td className="text-right px-2 py-1 tabular-nums text-stone-700">{itgRollup || ''}</td>
            <td className="text-right px-2 py-1 tabular-nums text-stone-400">
              {periodTarget(targets.ITG)}<TargetDot actual={itgRollup} target={periodTarget(targets.ITG)} />
            </td>
          </tr>
          {ITG_SUBS.map(sub => (
            <tr key={sub} className="border-t border-stone-100">
              <td className="px-2 py-1 pl-6 text-stone-600">{sub}</td>
              {days.map((d, i) => {
                const dk = fmtDate(d);
                const v = state.days[dk]?.hours?.[sub] || 0;
                return (
                  <td key={i} className={`px-1 py-1 text-center ${todayCls(i)}`}>
                    <HourInput value={v} onCommit={nv => setDayHours(dk, sub, nv)} />
                  </td>
                );
              })}
              <td className="text-right px-2 py-1 tabular-nums text-stone-700">{scalarTotal(sub) || ''}</td>
              <td></td>
            </tr>
          ))}

          <tr className="border-t border-stone-100">
            <td className="px-2 py-1 font-medium text-stone-700">
              <span className="inline-flex items-center">
                Clarys
                <button
                  onClick={() => addClarysRow(pKey)}
                  title="Add client"
                  className="ml-1.5 text-stone-400 theme-hover inline-flex items-center"
                >
                  <Plus size={11} />
                </button>
              </span>
            </td>
            {days.map((d, i) => {
              const dk = fmtDate(d);
              const v = state.days[dk]?.hours?.Clarys || 0;
              return (
                <td key={i} className={`px-1 py-1 text-center ${todayCls(i, clarysRows.length === 0 ? 'last' : undefined)}`}>
                  <HourInput value={v} onCommit={nv => setDayHours(dk, 'Clarys', nv)} />
                </td>
              );
            })}
            <td className="text-right px-2 py-1 tabular-nums text-stone-700">{clarysRollup || ''}</td>
            <td className="text-right px-2 py-1 tabular-nums text-stone-400">
              {periodTarget(targets.Clarys)}<TargetDot actual={clarysRollup} target={periodTarget(targets.Clarys)} />
            </td>
          </tr>
          {clarysRows.map((row, r) => (
            <tr key={row.id} className="border-t border-stone-100 group">
              <td className="px-2 py-1">
                <input
                  type="text"
                  value={row.client}
                  onChange={e => setClarysClient(pKey, row.id, e.target.value)}
                  placeholder="client"
                  className="w-full text-xs bg-transparent focus:bg-white focus:outline-none focus:ring-1 theme-ring rounded px-1.5 py-1 placeholder:text-stone-300"
                />
              </td>
              {days.map((d, i) => {
                const dk = fmtDate(d);
                return (
                  <td key={i} className={`${colClass} ${todayCls(i, r === clarysRows.length - 1 ? 'last' : undefined)}`}>
                    <HourInput
                      value={row.dayHours?.[dk] || 0}
                      onCommit={v => setClarysCell(pKey, row.id, dk, v)}
                    />
                  </td>
                );
              })}
              <td className="text-right px-2 py-1 tabular-nums text-stone-700">{clarysRowTotal(row) || ''}</td>
              <td className="text-right px-2 py-1">
                <button onClick={() => deleteClarysRow(pKey, row.id)} className="text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition" title="Remove client">
                  <X size={12} />
                </button>
              </td>
            </tr>
          ))}

          <tr className="no-stripe border-t-2 border-stone-300 theme-bar">
            <td colSpan={nCols - 2} className={`text-right px-2 py-1.5 ${lblClass} font-medium`}>Period total</td>
            <td className="text-right px-2 py-1.5 tabular-nums font-medium">{grandTotal || ''}</td>
            <td className="text-right px-2 py-1.5 tabular-nums text-stone-400">{periodTarget(Object.values(targets).reduce((a, b) => a + b, 0))}</td>
          </tr>
        </tbody>
      </table>
      <div className="px-3 py-2 flex items-center justify-between border-t border-stone-100 theme-bar text-xs">
        <button
          onClick={() => setPeriodAnchor(prevAnchor)}
          className="flex items-center gap-1 text-stone-500 theme-hover transition"
          title={`Previous period: ${prevLabel}`}
        >
          <ChevronLeft size={14} /> <span className="tabular-nums">{prevLabel}</span>
        </button>
        {!isCurrentPeriod && (
          <button
            onClick={() => setPeriodAnchor(null)}
            className="px-2 py-0.5 rounded text-stone-500 theme-hover hover:bg-stone-100 transition"
          >
            current period
          </button>
        )}
        <button
          onClick={() => setPeriodAnchor(nextAnchor)}
          className="flex items-center gap-1 text-stone-500 theme-hover transition"
          title={`Next period: ${nextLabel}`}
        >
          <span className="tabular-nums">{nextLabel}</span> <ChevronRight size={14} />
        </button>
      </div>
      {invoiceModal && (
        <ITGInvoiceModal
          data={invoiceModal}
          onChange={setInvoiceModal}
          onClose={() => setInvoiceModal(null)}
          onGenerate={async () => {
            try {
              await generateITGInvoice(invoiceModal);
              setInvoiceModal(null);
            } catch (err) {
              alert('Invoice generation failed: ' + err.message);
            }
          }}
        />
      )}
    </div>
  );
}

function ITGInvoiceModal({ data, onChange, onClose, onGenerate }) {
  const [busy, setBusy] = useState(false);
  const [yearStr, monthStr, halfStr] = data.periodKey.split('-');
  const monthName = MONTHS_FULL[parseInt(monthStr, 10) - 1];
  const startDay = halfStr === 'H1' ? 1 : 16;
  const endDay = halfStr === 'H1' ? 15 : new Date(parseInt(yearStr, 10), parseInt(monthStr, 10), 0).getDate();
  const periodLabel = `${monthName} ${startDay}-${endDay}, ${yearStr}`;

  const dougSub = data.dougHrs * ITG_RATE;
  const dustySub = data.dustyHrs * ITG_RATE;
  const total = (data.dougHrs + data.dustyHrs) * ITG_RATE;

  const dateInputValue = `${data.invoiceDate.getFullYear()}-${pad(data.invoiceDate.getMonth() + 1)}-${pad(data.invoiceDate.getDate())}`;

  const handleGenerate = async () => {
    setBusy(true);
    try { await onGenerate(); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl border border-stone-200 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="serif text-xl text-stone-800">ITG invoice</h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X size={16} /></button>
        </div>
        <div className="text-sm text-stone-600 mb-4">
          <div className="font-medium text-stone-800">{periodLabel}</div>
        </div>
        <div className="space-y-2 text-sm border border-stone-200 rounded p-3 mb-4 bg-stone-50">
          <div className="flex justify-between">
            <span className="text-stone-600">Doug (8232-999)</span>
            <span className="tabular-nums">{data.dougHrs} hrs · ${dougSub.toLocaleString()}</span>
          </div>
          {data.dustyHrs > 0 && (
            <div className="flex justify-between">
              <span className="text-stone-600">Dusty (108677.000.999)</span>
              <span className="tabular-nums">{data.dustyHrs} hrs · ${dustySub.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-stone-200 font-medium text-stone-800">
            <span>Total</span>
            <span className="tabular-nums">${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <label className="text-xs">
            <div className="uppercase tracking-wider text-stone-500 mb-1">Invoice no.</div>
            <input
              type="number"
              value={data.invoiceNo}
              onChange={(e) => onChange({ ...data, invoiceNo: parseInt(e.target.value, 10) || 0 })}
              className="w-full border border-stone-300 rounded px-2 py-1 tabular-nums text-sm"
            />
          </label>
          <label className="text-xs">
            <div className="uppercase tracking-wider text-stone-500 mb-1">Invoice date</div>
            <input
              type="date"
              value={dateInputValue}
              onChange={(e) => {
                const [y, m, d] = e.target.value.split('-').map(Number);
                onChange({ ...data, invoiceDate: new Date(y, m - 1, d) });
              }}
              className="w-full border border-stone-300 rounded px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-stone-600 hover:text-stone-800">Cancel</button>
          <button
            onClick={handleGenerate}
            disabled={busy}
            className="px-3 py-1.5 text-sm bg-emerald-800 hover:bg-emerald-900 text-white rounded disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Generate .docx
          </button>
        </div>
      </div>
    </div>
  );
}

function MonthView({ anchor, today, state, selectedDay, onSelectDay, setAnchor, setView }) {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-stone-400 text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map(d => {
          const key = fmtDate(d);
          const day = state.days[key];
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = key === selectedDay;
          const hasItems = day?.items?.length > 0;
          const doneCount = day?.items?.filter(i => i.done).length || 0;
          const total = day?.items?.length || 0;
          return (
            <button
              key={key}
              onClick={() => onSelectDay(key)}
              onDoubleClick={() => { setAnchor(d); onSelectDay(key); setView('week'); }}
              style={isSelected ? { backgroundColor: 'var(--theme-tint)' } : undefined}
              className={`aspect-square p-2 text-left rounded-lg border transition flex flex-col ${
                isSelected ? 'border-emerald-900 shadow-sm' :
                isToday ? 'border-stone-300 bg-white' :
                'border-transparent hover:border-stone-200 bg-white/40'
              } ${!inMonth ? 'opacity-30' : ''}`}
            >
              <div className={`serif text-base leading-none ${isToday ? 'text-emerald-900' : 'text-stone-700'}`}>{d.getDate()}</div>
              {hasItems && (
                <div className="mt-auto flex items-center gap-1">
                  <div className="flex-1 h-1 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-800" style={{ width: `${(doneCount/total)*100}%` }} />
                  </div>
                  <span className="text-[9px] text-stone-400 tabular-nums">{total}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ anchor, today, state, setAnchor, setView }) {
  const year = anchor.getFullYear();
  const quarters = [1, 2, 3, 4];
  return (
    <div className="space-y-4">
      {quarters.map(q => {
        const qKey = `${year}-Q${q}`;
        const qData = state.quarters[qKey];
        const qStartMonth = (q - 1) * 3;
        const isCurrent = today.getFullYear() === year && Math.floor(today.getMonth() / 3) + 1 === q;
        return (
          <div key={qKey} className={`border rounded-xl p-4 ${isCurrent ? 'border-emerald-900 bg-white' : 'border-stone-200 bg-white/50'}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div className="flex items-baseline gap-3">
                <h3 className="serif text-xl tracking-tight">Q{q}</h3>
                <span className="text-xs text-stone-500">{MONTHS[qStartMonth]} – {MONTHS[qStartMonth + 2]} {year}</span>
              </div>
              {isCurrent && <span className="text-[10px] uppercase tracking-wider text-emerald-900 font-medium">Current</span>}
            </div>
            {qData?.focus && <p className="text-sm text-stone-700 mb-3 leading-relaxed">{qData.focus}</p>}
            {qData?.milestones?.length > 0 && (
              <ul className="space-y-1">
                {qData.milestones.map((m, i) => (
                  <li key={i} className="text-xs text-stone-600 flex gap-2">
                    <span className="text-stone-300">◇</span><span>{m}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-stone-100">
              {[0, 1, 2].map(offset => {
                const m = qStartMonth + offset;
                const monthDate = new Date(year, m, 1);
                return (
                  <button
                    key={m}
                    onClick={() => { setAnchor(monthDate); setView('month'); }}
                    className="text-xs text-stone-500 hover:text-stone-900 hover:bg-stone-50 rounded py-1.5 transition"
                  >
                    {MONTHS[m]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodayPanel({ dayKey, day, isToday, addItem, toggleItem, deleteItem, editItem, reorderItems, enableDrag, setNotes, setSelectedDay, setAnchor, today }) {
  const [newItem, setNewItem] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => parseDate(dayKey));
  const pickerRef = useRef(null);
  const d = parseDate(dayKey);
  const items = day?.items || [];
  const doneCount = items.filter(i => i.done).length;

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const editInputRef = useRef(null);
  useEffect(() => {
    if (editingId != null) editInputRef.current?.focus();
  }, [editingId]);
  useEffect(() => { setEditingId(null); }, [dayKey]);
  const startEdit = (item) => { setEditingId(item.id); setEditText(item.text); };
  const commitEdit = (id) => {
    if (editText.trim()) editItem && editItem(dayKey, id, editText);
    setEditingId(null); setEditText('');
  };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [touchDragIdx, setTouchDragIdx] = useState(null);
  const touchDragRef = useRef(null);
  const dragOverIdxRef = useRef(null);
  const longPressRef = useRef(null);
  const touchStartRef = useRef(null);
  const movedRef = useRef(false);
  const justActivatedRef = useRef(false);

  useEffect(() => { dragOverIdxRef.current = dragOverIdx; }, [dragOverIdx]);

  const cancelLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };

  useEffect(() => {
    if (touchDragIdx == null) return;
    const onMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const li = el?.closest?.('[data-item-idx]');
      const raw = li?.dataset?.itemIdx;
      const idx = raw != null ? parseInt(raw, 10) : null;
      if (idx !== dragOverIdxRef.current) setDragOverIdx(idx);
    };
    const onEnd = () => {
      const drag = touchDragRef.current;
      const overIdx = dragOverIdxRef.current;
      if (drag != null && overIdx != null && overIdx !== drag.from) {
        reorderItems && reorderItems(dayKey, drag.from, overIdx);
      }
      touchDragRef.current = null;
      setDragOverIdx(null);
      setTouchDragIdx(null);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [touchDragIdx, dayKey, reorderItems]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const openPicker = () => {
    setPickerMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setPickerOpen(o => !o);
  };
  const pickDate = (date) => {
    const k = fmtDate(date);
    setSelectedDay && setSelectedDay(k);
    setAnchor && setAnchor(date);
    setPickerOpen(false);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 fade-in">
      <div className="flex items-baseline justify-between mb-3 relative">
        <button
          type="button"
          onClick={openPicker}
          aria-label="Pick a date"
          aria-expanded={pickerOpen}
          className="text-left group -m-1 p-1 rounded hover:bg-stone-50 transition"
        >
          <div className="text-[10px] uppercase tracking-wider text-stone-400">
            {isToday ? 'Today' : DAYS_SHORT[(d.getDay() + 6) % 7]}
          </div>
          <div className="serif text-lg tracking-tight flex items-center gap-1 group-hover:text-emerald-900 transition">
            {MONTHS[d.getMonth()]} {d.getDate()}
            <ChevronDown size={14} className="text-stone-400 group-hover:text-emerald-900 transition" />
          </div>
        </button>
        {items.length > 0 && (
          <div className="text-xs text-stone-500 tabular-nums">{doneCount}/{items.length}</div>
        )}
        {pickerOpen && (
          <div ref={pickerRef} className="absolute left-0 top-full mt-2 z-30">
            <DatePickerPopover
              selected={d}
              today={today}
              month={pickerMonth}
              setMonth={setPickerMonth}
              onPick={pickDate}
            />
          </div>
        )}
      </div>

      <ul className="space-y-1.5 mb-3">
        {items.length === 0 && <li className="text-xs text-stone-400 italic">No items yet.</li>}
        {items.map((item, idx) => {
          const isEditing = editingId === item.id;
          const isDragging = draggedIdx === idx || touchDragIdx === idx;
          const showDropLine = dragOverIdx === idx && (draggedIdx != null || touchDragIdx != null) && (draggedIdx ?? touchDragIdx) !== idx;
          return (
            <li
              key={item.id}
              data-item-idx={idx}
              draggable={enableDrag && !isEditing}
              onDragStart={enableDrag ? (e) => {
                if (isEditing) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('application/x-operator-todaypanel-idx', String(idx));
                setDraggedIdx(idx);
              } : undefined}
              onDragOver={enableDrag ? (e) => {
                if (draggedIdx == null) return;
                if (!e.dataTransfer.types.includes('application/x-operator-todaypanel-idx')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverIdx !== idx) setDragOverIdx(idx);
              } : undefined}
              onDragLeave={enableDrag ? (e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (dragOverIdx === idx) setDragOverIdx(null);
              } : undefined}
              onDrop={enableDrag ? (e) => {
                e.preventDefault();
                const raw = e.dataTransfer.getData('application/x-operator-todaypanel-idx');
                const from = raw === '' ? null : parseInt(raw, 10);
                if (from != null && !Number.isNaN(from) && from !== idx) {
                  reorderItems && reorderItems(dayKey, from, idx);
                }
                setDraggedIdx(null);
                setDragOverIdx(null);
              } : undefined}
              onDragEnd={enableDrag ? () => { setDraggedIdx(null); setDragOverIdx(null); } : undefined}
              onTouchStart={(e) => {
                if (isEditing || enableDrag) return;
                if (touchDragRef.current) return;
                if (e.target.closest('button, input, textarea, a')) return;
                const t = e.touches[0];
                touchStartRef.current = { x: t.clientX, y: t.clientY };
                movedRef.current = false;
                justActivatedRef.current = false;
                cancelLongPress();
                longPressRef.current = setTimeout(() => {
                  longPressRef.current = null;
                  if (movedRef.current) return;
                  if (navigator.vibrate) navigator.vibrate(10);
                  justActivatedRef.current = true;
                  touchDragRef.current = { from: idx };
                  setTouchDragIdx(idx);
                }, 400);
              }}
              onTouchMove={(e) => {
                if (touchDragRef.current) return;
                const t = e.touches[0];
                if (!t || !touchStartRef.current) return;
                const dx = Math.abs(t.clientX - touchStartRef.current.x);
                const dy = Math.abs(t.clientY - touchStartRef.current.y);
                if (dx > 8 || dy > 8) { movedRef.current = true; cancelLongPress(); }
              }}
              onTouchEnd={cancelLongPress}
              onTouchCancel={cancelLongPress}
              className={`group flex items-start gap-2 text-sm relative ${isDragging ? 'opacity-60' : ''} ${touchDragIdx === idx ? 'scale-[1.02] font-medium' : ''} transition-transform`}
              style={{ WebkitTouchCallout: 'none', WebkitUserSelect: isEditing ? 'auto' : 'none' }}
            >
              {showDropLine && (
                <span className="absolute -top-[3px] left-0 right-0 h-0.5 bg-emerald-400 rounded pointer-events-none" />
              )}
              {enableDrag && (
                <span
                  className="flex-shrink-0 mt-0.5 text-stone-300 opacity-0 group-hover:opacity-100 transition cursor-grab active:cursor-grabbing"
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={14} />
                </span>
              )}
              <button
                onClick={() => toggleItem(dayKey, item.id)}
                className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded border ${item.done ? 'bg-emerald-800 border-emerald-800' : 'border-stone-300 hover:border-stone-500'} flex items-center justify-center transition`}
              >
                {item.done && <Check size={11} className="text-white" />}
              </button>
              {isEditing ? (
                <input
                  ref={editInputRef}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(item.id); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                  }}
                  onBlur={() => commitEdit(item.id)}
                  className="flex-1 leading-snug text-sm bg-stone-50 border border-stone-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-stone-500"
                />
              ) : (
                <span
                  onClick={() => {
                    if (justActivatedRef.current) { justActivatedRef.current = false; return; }
                    startEdit(item);
                  }}
                  className={`flex-1 leading-snug cursor-text ${item.done ? 'text-stone-400 line-through' : 'text-stone-800'}`}
                >
                  {item.text}
                </span>
              )}
              {!isEditing && (
                <button
                  onClick={() => deleteItem(dayKey, item.id)}
                  className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 transition"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <input
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { addItem(dayKey, newItem); setNewItem(''); } }}
          placeholder="Add action item…"
          className="flex-1 text-sm px-3 py-1.5 bg-stone-50 border border-stone-200 rounded focus:outline-none focus:border-stone-400"
        />
        <button
          onClick={() => { addItem(dayKey, newItem); setNewItem(''); }}
          className="px-3 bg-stone-900 text-white rounded hover:bg-stone-700 transition"
        >
          <Plus size={14} />
        </button>
      </div>

      <textarea
        value={day?.notes || ''}
        onChange={e => setNotes(dayKey, e.target.value)}
        placeholder="Notes, blockers, wins…"
        className="mt-3 w-full text-xs p-2 bg-stone-50 border border-stone-200 rounded resize-none focus:outline-none focus:border-stone-400"
        rows={2}
      />
    </div>
  );
}

function DatePickerPopover({ selected, today, month, setMonth, onPick }) {
  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const selKey = fmtDate(selected);
  const todayKey = fmtDate(today);
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-lg p-3 w-[260px]">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, -1))}
          className="p-1 rounded hover:bg-stone-100 text-stone-500"
          aria-label="Previous month"
        >
          <ChevronLeft size={14} />
        </button>
        <div className="serif text-sm tracking-tight">
          {MONTHS_FULL[month.getMonth()]} {month.getFullYear()}
        </div>
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, 1))}
          className="p-1 rounded hover:bg-stone-100 text-stone-500"
          aria-label="Next month"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAYS_SHORT.map(dn => (
          <div key={dn} className="text-[9px] uppercase tracking-wider text-stone-400 text-center py-1">{dn[0]}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map(d => {
          const k = fmtDate(d);
          const inMonth = d.getMonth() === month.getMonth();
          const isToday = k === todayKey;
          const isSelected = k === selKey;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onPick(d)}
              className={`aspect-square text-xs rounded transition flex items-center justify-center tabular-nums ${
                isSelected ? 'bg-emerald-900 text-white' :
                isToday ? 'bg-stone-100 text-emerald-900 font-medium' :
                'text-stone-700 hover:bg-stone-100'
              } ${!inMonth ? 'opacity-30' : ''}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

function NotesList({ notes, updateNotes, clearAll, compact }) {
  const [editing, setEditing] = useState({ id: null, text: '' });
  const textareaRef = useRef(null);
  const pendingCaret = useRef(null);
  const skipBlur = useRef(false);

  useEffect(() => {
    if (editing.id && textareaRef.current) {
      const el = textareaRef.current;
      autoResize(el);
      el.focus();
      const c = pendingCaret.current;
      if (typeof c === 'number') {
        el.setSelectionRange(c, c);
      } else {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      pendingCaret.current = null;
    }
  }, [editing.id]);

  const startEdit = (n) => setEditing({ id: n.id, text: n.text });

  const startNewBullet = () => {
    const newId = Date.now() + Math.random();
    updateNotes(ns => [...ns, { id: newId, text: '' }]);
    setEditing({ id: newId, text: '' });
  };

  const commit = () => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    setEditing(prev => {
      if (!prev.id) return prev;
      if (prev.text === '') {
        updateNotes(ns => ns.filter(n => n.id !== prev.id));
      } else {
        updateNotes(ns => ns.map(n => n.id === prev.id ? { ...n, text: prev.text } : n));
      }
      return { id: null, text: '' };
    });
  };

  const cancel = () => {
    skipBlur.current = true;
    setEditing(prev => {
      if (prev.id && prev.text === '') {
        updateNotes(ns => ns.filter(n => n.id !== prev.id));
      }
      return { id: null, text: '' };
    });
  };

  const onChangeEditing = (e) => {
    const v = e.target.value;
    setEditing(prev => ({ ...prev, text: v }));
    autoResize(e.target);
  };

  const handleKeyDown = (e) => {
    const el = e.target;
    const caret = el.selectionStart;
    const caretEnd = el.selectionEnd;

    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }

    if (e.key === 'Enter') {
      const text = editing.text;
      const isEmpty = text.length === 0;
      const prevIsNewline = caret > 0 && text[caret - 1] === '\n';

      // Enter on an empty bullet → finalize: drop the empty bullet + exit edit mode
      if (isEmpty) {
        e.preventDefault();
        const curId = editing.id;
        skipBlur.current = true;
        updateNotes(ns => ns.filter(n => n.id !== curId));
        setEditing({ id: null, text: '' });
        return;
      }

      // Enter right after a newline → split into a new bullet below
      if (prevIsNewline) {
        e.preventDefault();
        const splitStart = caret - 1;
        const left = text.slice(0, splitStart);
        const right = text.slice(caret);
        const newId = Date.now() + Math.random();
        skipBlur.current = true;
        updateNotes(ns => {
          const i = ns.findIndex(n => n.id === editing.id);
          if (i < 0) return ns;
          const out = [...ns];
          out[i] = { ...out[i], text: left };
          out.splice(i + 1, 0, { id: newId, text: right });
          return out;
        });
        pendingCaret.current = 0;
        setEditing({ id: newId, text: right });
        return;
      }
      // otherwise: default newline
    }

    if (e.key === 'Backspace' && caret === 0 && caretEnd === 0) {
      const i = notes.findIndex(n => n.id === editing.id);
      if (i < 0) return;
      if (i === 0) {
        if (editing.text === '' && notes.length > 1) {
          e.preventDefault();
          const next = notes[1];
          skipBlur.current = true;
          updateNotes(ns => ns.filter(n => n.id !== editing.id));
          pendingCaret.current = 0;
          setEditing({ id: next.id, text: next.text });
        }
        return;
      }
      e.preventDefault();
      const prev = notes[i - 1];
      const mergePoint = prev.text.length;
      const mergedText = prev.text + editing.text;
      skipBlur.current = true;
      updateNotes(ns => {
        const j = ns.findIndex(n => n.id === editing.id);
        if (j <= 0) return ns;
        const out = [...ns];
        out[j - 1] = { ...out[j - 1], text: mergedText };
        out.splice(j, 1);
        return out;
      });
      pendingCaret.current = mergePoint;
      setEditing({ id: prev.id, text: mergedText });
    }
  };

  const removeNote = (id) => {
    if (editing.id === id) { skipBlur.current = true; setEditing({ id: null, text: '' }); }
    updateNotes(ns => ns.filter(n => n.id !== id));
  };

  const textSize = compact ? 'text-sm' : 'text-sm';
  const listMax = compact ? 'max-h-64' : 'flex-1';

  return (
    <div className={`bg-white border border-stone-200 rounded-xl p-4 flex flex-col space-y-2 fade-in ${compact ? '' : 'h-[70vh]'}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium" style={{ color: '#E8963A' }}>
        <StickyNote size={14} />Notes
      </div>
      <ul className={`${listMax} overflow-y-auto scrollbar-thin space-y-1 pr-1`}>
        {notes.map((n) => (
          <li key={n.id} className={`group flex items-start gap-2 ${textSize} text-stone-700`}>
            <span className="text-stone-300 mt-0.5 flex-shrink-0 leading-snug">•</span>
            {editing.id === n.id ? (
              <textarea
                ref={textareaRef}
                value={editing.text}
                onChange={onChangeEditing}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                rows={1}
                className={`flex-1 ${textSize} bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 resize-none overflow-hidden focus:outline-none focus:border-stone-400 leading-snug`}
              />
            ) : (
              <span
                className="flex-1 leading-snug whitespace-pre-wrap break-words cursor-text"
                onClick={() => startEdit(n)}
              >
                {n.text}
              </span>
            )}
            <button
              onClick={() => removeNote(n.id)}
              className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 transition flex-shrink-0"
              aria-label="Remove note"
              tabIndex={-1}
            >
              <X size={12} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={startNewBullet}
        className={`${textSize} text-stone-400 italic hover:text-stone-600 transition text-left flex items-center gap-2`}
      >
        <span className="text-stone-300 flex-shrink-0">•</span>
        <span>Add a note…</span>
      </button>
      {notes.length > 0 && (
        <button
          onClick={clearAll}
          className="text-[11px] text-stone-400 hover:text-red-600 transition self-start"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function ChatPanel({ dayKey, messages, input, setInput, send, loading, error, onAddItem, onApplyHours, onClear, chatEndRef, mobileOnly }) {
  return (
    <div className={`bg-white border border-stone-200 rounded-xl flex flex-col ${mobileOnly ? 'h-[70vh]' : 'h-[520px]'}`}>
      <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
        <Sparkles size={14} className="text-emerald-800" />
        <span className="text-[10px] uppercase tracking-wider text-stone-400 font-medium">Claude · context: {dayKey}</span>
        <button
          onClick={onClear}
          disabled={!messages.length}
          title="Clear chat thread"
          className="ml-auto text-stone-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-stone-300 transition"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-xs text-stone-400 italic text-center py-8">
            Start a check-in. Tell Claude what you worked on, what's next, or ask for a strategic read.
          </div>
        )}
        {messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const showDate = m.date && (!prev || prev.date !== m.date);
          return (
            <React.Fragment key={i}>
              {showDate && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-stone-100" />
                  <span className="text-[10px] uppercase tracking-wider text-stone-400 font-medium">{m.date}</span>
                  <div className="flex-1 h-px bg-stone-100" />
                </div>
              )}
              <Message message={m} onAddItem={onAddItem} onApplyHours={onApplyHours} />
            </React.Fragment>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Loader2 size={12} className="animate-spin" /> Thinking…
          </div>
        )}
        {error && <div className="text-xs text-red-600">{error}</div>}
        <div ref={chatEndRef} />
      </div>
      <div className="p-3 border-t border-stone-100 flex gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Check in, ask, plan…"
          className="flex-1 text-sm px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg resize-none focus:outline-none focus:border-stone-400 max-h-24"
          rows={1}
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          className="px-3 bg-emerald-900 text-white rounded-lg hover:bg-emerald-800 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

function Message({ message, onAddItem, onApplyHours }) {
  const isUser = message.role === 'user';
  const items = !isUser ? extractListItems(message.content, message.date) : [];
  const hourItems = !isUser ? extractHoursItems(message.content) : [];
  const [applied, setApplied] = useState(new Set());
  const [appliedItems, setAppliedItems] = useState(new Set());
  const visibleContent = !isUser
    ? message.content.replace(/\[HOURS:[^\]]+\]/g, '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
    : message.content;
  return (
    <div className={`fade-in ${isUser ? 'flex justify-end' : ''}`}>
      <div className={`${isUser ? 'bg-stone-900 text-white max-w-[85%]' : 'text-stone-800 max-w-full'} rounded-2xl ${isUser ? 'px-3.5 py-2' : ''}`}>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{visibleContent}</div>
        {!isUser && items.length > 0 && (
          <div className="mt-2 pt-2 border-t border-stone-100 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Add to calendar:</div>
            {items.map((it, i) => {
              const isApplied = appliedItems.has(i);
              const badge = formatDateBadge(it.dates);
              return (
                <button
                  key={i}
                  disabled={isApplied}
                  onClick={() => { onAddItem(it.text, it.dates); setAppliedItems(prev => new Set(prev).add(i)); }}
                  style={{ backgroundColor: isApplied ? '#064E3B' : 'var(--theme-tint)' }}
                  onMouseEnter={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-accent)'; }}
                  onMouseLeave={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-tint)'; }}
                  className={`w-full text-left text-xs px-2 py-1 rounded flex items-start gap-1.5 ${
                    isApplied ? 'text-white cursor-default' : 'text-emerald-900 group'
                  }`}
                >
                  {isApplied ? <Check size={11} className="flex-shrink-0 mt-0.5" /> : <Plus size={11} className="flex-shrink-0 mt-0.5" />}
                  <span className="flex-1 leading-snug">
                    {badge && (
                      <span className={`font-medium mr-1.5 tabular-nums ${isApplied ? 'text-emerald-100' : 'text-emerald-700/70'}`}>{badge}</span>
                    )}
                    <span>{it.text}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {!isUser && hourItems.length > 0 && (
          <div className="mt-2 pt-2 border-t border-stone-100 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Log hours:</div>
            {hourItems.map((it, i) => {
              const isApplied = applied.has(i);
              const d = parseDate(it.date);
              const badge = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
              const streamLabel = it.client ? `${it.stream}: ${it.client}` : it.stream;
              return (
                <button
                  key={i}
                  disabled={isApplied}
                  onClick={() => { onApplyHours(it); setApplied(prev => new Set(prev).add(i)); }}
                  style={{ backgroundColor: isApplied ? '#064E3B' : 'var(--theme-tint)' }}
                  onMouseEnter={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-accent)'; }}
                  onMouseLeave={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-tint)'; }}
                  className={`w-full text-left text-xs px-2 py-1 rounded flex items-start gap-1.5 ${
                    isApplied ? 'text-white cursor-default' : 'text-emerald-900'
                  }`}
                >
                  {isApplied ? <Check size={11} className="flex-shrink-0 mt-0.5" /> : <Plus size={11} className="flex-shrink-0 mt-0.5" />}
                  <span className="flex-1 leading-snug">
                    <span className={`font-medium mr-1.5 tabular-nums ${isApplied ? 'text-emerald-100' : 'text-emerald-700/70'}`}>{badge}</span>
                    <span>{isApplied ? 'Added' : 'Add'} {it.amount}h to {streamLabel}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
